import { faSave } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormikErrors, useFormik } from 'formik';
import { chain } from 'lodash';
import React, { useMemo } from 'react';
import VoxelButton from '../../../components/voxel/Buttons/VoxelButton';
import VoxelAutocomplete, {
  GenericOption,
} from '../../../components/voxel/Forms/VoxelAutocomplete';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import { CurrentTeamGq } from '../../../graphql/users/models/CurrentTeamGq';
import { CurrentUserGq } from '../../../graphql/users/models/CurrentUserGq';
import { usersApiService } from '../../../graphql/users/usersApiService';

export interface AllowedDomainsFormProps {
  user: CurrentUserGq;
  team: CurrentTeamGq;
  reload: () => Promise<unknown>;
}

interface FormState {
  allowedDomains: string[];
}

const AllowedDomainsForm: React.FC<AllowedDomainsFormProps> = ({ user, team, reload }) => {
  // Team settings form
  const handleUpdateTeamOptions = usersApiService.mutations.useEditAllowedDomains();
  const formikProps = useFormik<FormState>({
    initialValues: {
      allowedDomains: team.allowedEmailDomains ?? [],
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: FormikErrors<FormState> = {};
      return errors;
    },
    onSubmit: (values, actions) =>
      handleUpdateTeamOptions({
        teamId: team.id,
        allowedEmailDomains: values.allowedDomains,
      })
        .catch((reason: any) => console.error(reason))
        .then(() => reload()),
  });
  const getEmailDomain = (user?: CurrentUserGq) => {
    const domain = user?.email?.split('@')[1]?.trim()?.toLowerCase().trim();
    const invalidDomains = ['gmail.com', 'outlook.com', 'yahoo.com', 'hotmail.com', 'aol.com'];
    return !domain?.length || invalidDomains.includes(domain) ? undefined : '@' + domain;
  };

  const {
    query,
    users: teamMembers,
    paging,
  } = usersApiService.queries.useGetTeamUsers({
    pagingInput: { page: 0, amountPerPage: 50 },
    searchTerm: '',
    members: true,
  });

  const relevantDomains = useMemo(() => {
    // Only allow this feature for normal teams (not for custom subscriptions with invoices, etc.)
    /* if (
      [AppTierTypeGq.BASIC, AppTierTypeGq.FREE_TRIAL, AppTierTypeGq.SPONSORED].includes(
        team?.appTier?.tierType
      )
    ) {
      return [];
    } */
    const list = [...(team.allowedEmailDomains ?? [])];
    return chain(teamMembers)
      .map((tm) => getEmailDomain(tm))
      .uniq()
      .union(list)
      .filter((d) => !!d?.length)
      .orderBy((v) => v)
      .value();
  }, [teamMembers, team.allowedEmailDomains]);

  const authorized = !!user.team.accessUserCanEditMembersUpTo;

  const options: GenericOption[] = relevantDomains.map((d) => ({
    value: d,
    title: d,
  }));

  return (
    <div className="flex flex-col gap-2">
      <VoxelText variant="bread-semibold">Allowed email domains</VoxelText>
      <div className="w-full flex flex-row items-center gap-3">
        <div className="grow">
          <VoxelAutocomplete
            multi
            freeAdd
            options={options}
            disabled={!authorized}
            selectedOptionValues={formikProps.values.allowedDomains}
            setSelectedOptions={(values) => {
              const mapped = values.map((t) => t.value);
              formikProps.setFieldValue('allowedDomains', mapped);
            }}
            searchPlaceholder="Search or Add"
            nrVisibleMultiOptions={5}
          />
        </div>
        <VoxelButton
          title={'Save'}
          endIcon={<FontAwesomeIcon icon={faSave} />}
          shape="square"
          color="success"
          onClick={formikProps.submitForm}
          disabled={!formikProps.dirty || formikProps.isSubmitting || !authorized}
        />
      </div>
      <VoxelText variant="form-caption">
        Anyone with email addresses at these domains may automatically join your team
      </VoxelText>
    </div>
  );
};

export default AllowedDomainsForm;
